/**
 * boxColliders.ts — the oriented-box collision field, independent of what put the
 * boxes there.
 *
 * Extracted verbatim from settlementCollision.ts (W2b-2.5 Task 2), whose
 * {@link SettlementColliders} now extends this base. NOTHING here changed in the
 * move: the slab tests, the tie-break ordering, the push cap, the smear rings and
 * the bucket sizing are the same code, so `test:worldgen-collision` and
 * `test:homestead-collision` pin it.
 *
 * WHY THE SPLIT. The query half of the field — {@link BoxColliderField.resolve},
 * {@link BoxColliderField.occludes}, {@link BoxColliderField.standRadius},
 * {@link BoxColliderField.segmentHit} — is pure {@link BoxFeat} math and needs
 * nothing but a planet radius. Only the INGESTION half was settlement-specific:
 * `SettlementColliders` owns a sampler, a carve index, and `addRegion(g: CityGraph)`,
 * which was the sole public way to get geometry in. Anything else wanting solid
 * boxes on a sphere — caches, and any later authored space — had no seam.
 * {@link BoxColliderField.addBox} is that seam.
 *
 * Consumers importing from `@hyperlight/shared` are unaffected: settlementCollision.ts
 * re-exports the constants that moved, and worldgen/index.ts still re-exports it.
 *
 * SHARED-CODE DISCIPLINE: pmath only (hypot/cos/sin/TWO_PI); distances use the
 * spec-exact Math.sqrt. No banned Math.* / `**` — the pmath-ban gate covers this file.
 */
import * as pmath from "../pmath.js";

// ── knobs (binding) ────────────────────────────────────────────────────────────

/**
 * Walkable top band (metres) exempted from the lateral push on STANDABLE boxes
 * (building roofs). A capsule whose reference point sits within this of a roof
 * top is treated as STANDING ON the roof — the ground clamp (roofStand) owns it —
 * NOT as embedded in the wall, so `resolve` must not shove it laterally off the
 * edge. Without this, a capsule at the roofline (qh ≈ +hh) fell inside the push
 * band (qh ≤ hh + radius) and was ejected sideways: the "pushed off the roof
 * before landing / roofs not standable" bug. Non-standable retaining walls keep
 * the original full-height solid gate (you never stand on them). Sized above the
 * character's ground-clamp catch window so there is no seam where resolve pushes
 * a descending body the clamp hasn't yet caught.
 */
export const ROOF_STEP_M = 0.9;
/** Max push-out applied per resolve() call (metres) — mirrors resolveTownCollision's
 *  cap (towns.ts): a body deep inside a solid can't teleport multi-metres in one
 *  tick; repeated calls converge it out one bounded step at a time. */
export const MAX_PUSH_M = 0.6;
/** Max feature bounding radius + query reach (metres) — sizes the bucket grid and
 *  guards the single-bucket smear invariant (mirrors carveindex's CARVE_MAX_M). */
export const COLLIDER_MAX_M = 40;
/** Extra smear margin (metres) beyond a feature's bound for the query capsule. */
const QUERY_MARGIN_M = 2;

// ── tangent frame (shared with structures.ts / the renderer's reconstruction) ──

/**
 * Right-handed tangent frame at planet-local point (px,py,pz): `up` is the outward
 * radial, `t1`/`t2` span the tangent plane. Byte-identical to the frame
 * structures.ts encodes building/stair yaw against, so a yaw decoded here rebuilds
 * the exact axes the renderer draws. pmath-pure (shared-code discipline).
 */
export function tangentFrame(px: number, py: number, pz: number): { up: number[]; t1: number[]; t2: number[] } {
  const l = pmath.hypot(px, py, pz) || 1;
  const up = [px / l, py / l, pz / l];
  const rx = Math.abs(up[1]) < 0.9 ? 0 : 1;
  const ry = Math.abs(up[1]) < 0.9 ? 1 : 0;
  let t1x = ry * up[2] - 0 * up[1];
  let t1y = 0 * up[0] - rx * up[2];
  let t1z = rx * up[1] - ry * up[0];
  const l1 = pmath.hypot(t1x, t1y, t1z) || 1;
  t1x /= l1; t1y /= l1; t1z /= l1;
  const t2x = up[1] * t1z - up[2] * t1y;
  const t2y = up[2] * t1x - up[0] * t1z;
  const t2z = up[0] * t1y - up[1] * t1x;
  return { up, t1: [t1x, t1y, t1z], t2: [t2x, t2y, t2z] };
}

/**
 * Cube-face bucket id of a unit direction at grid resolution G — a verbatim port of
 * the carve index's `cellOfG` (itself rivers' `cellOf`): same dir → same cell,
 * nearby dirs → nearby cells, seams handled by smearing.
 */
function cellOfG(x: number, y: number, z: number, G: number): number {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const az = Math.abs(z);
  let face: number;
  let u: number;
  let v: number;
  if (ax >= ay && ax >= az) {
    const s = 1 / ax;
    face = x > 0 ? 0 : 1;
    u = (x > 0 ? -z : z) * s;
    v = y * s;
  } else if (ay >= az) {
    const s = 1 / ay;
    face = y > 0 ? 2 : 3;
    u = x * s;
    v = (y > 0 ? -z : z) * s;
  } else {
    const s = 1 / az;
    face = z > 0 ? 4 : 5;
    u = (z > 0 ? x : -x) * s;
    v = y * s;
  }
  const cu = Math.min(G - 1, Math.max(0, ((u * 0.5 + 0.5) * G) | 0));
  const cv = Math.min(G - 1, Math.max(0, ((v * 0.5 + 0.5) * G) | 0));
  return (face * G + cv) * G + cu;
}

/** An oriented box: centre, orthonormal axes (width/up/depth), half-extents. */
interface BoxFeat {
  cx: number; cy: number; cz: number;   // box centre (planet-local metres)
  ux: number; uy: number; uz: number;   // "up" axis (radial) — the HEIGHT axis
  wx: number; wy: number; wz: number;   // width axis (unit)
  dx: number; dy: number; dz: number;   // depth axis (unit)
  hw: number; hh: number; hd: number;   // half width / half height / half depth
  ex: number; ey: number; ez: number;   // conservative world-axis half-extents
  reach: number;                        // bounding radius (smear + broad reject)
  standable: boolean;                   // buildings have walkable flat tops; walls do not
  planarSupport: boolean;
}

/** One oriented box to register via {@link BoxColliderField.addBox}. */
export interface BoxSpec {
  /** Intersect the radial query with the actual top plane. Use for large flat
   * decks where treating the top as a constant radius visibly curves support. */
  planarSupport?: boolean;
  /** BASE-surface centre in planet-local metres (a footprint centre / wall mid-base). */
  base: [number, number, number];
  /** Outward radial at the base (unit) — the height axis. */
  up: [number, number, number];
  /** Width axis (unit, perpendicular to `up`). Depth is derived as up × width. */
  width: [number, number, number];
  halfWidth: number;
  /** FULL height above the base: the box spans [base, base + height] radially. */
  height: number;
  halfDepth: number;
  /** True for walkable flat tops (roofs, floors, treads); false for walls. */
  standable: boolean;
}

/**
 * A bucketed field of oriented solid boxes on a sphere, answering the spatial queries
 * the game runs against solid geometry — the settlement analogues of the legacy town
 * collision family (towns.ts):
 *
 *   - {@link resolve} — push a capsule (character/drone) OUT of any box it overlaps.
 *     2.5-D lateral push-out, mirroring `resolveTownCollision`: a point over a
 *     roofline is ignored, the cheapest exit face that is not inside another box
 *     wins, and the applied push is returned via `out` (the corrected position).
 *   - {@link segmentHit} — the first fraction t∈[0,1] a segment (bolt / ship) crosses
 *     a solid, or -1, mirroring `townSegmentHit`.
 *
 * INDEX. Each box is smeared into every cube-face bucket its bound reaches
 * ({@link cellOfG}, a verbatim port of the carve index's grid) so a query is a
 * single-bucket scan. A brute path over ALL features ({@link resolveBrute} /
 * {@link segmentHitBrute}) backs the brute==hash determinism oracle exactly as
 * `carveAtBrute` does for the carve index.
 *
 * Feed it with {@link addBox}, or subclass it for a domain-specific fold (see
 * `SettlementColliders.addRegion`).
 */
export class BoxColliderField {
  protected readonly R: number;
  /** Bucket count per cube-face axis (see the constructor). */
  private readonly G: number;
  /** One bucket's arc width, metres — the smear margin unit. */
  private readonly cellWidthM: number;

  /** bucket id → feature indices overlapping it (+ smear margin). */
  private readonly grid = new Map<number, number[]>();
  /** All box features, in fold order. */
  private readonly feats: BoxFeat[] = [];
  private readonly emptyBucket: number[] = [];

  constructor(radius: number) {
    this.R = radius;
    // Same bucket sizing as SettlementIndex: the largest G whose bucket width
    // (R·π/2)/G is still ≥ 2·COLLIDER_MAX_M, so a feature's ≤COLLIDER_MAX_M reach
    // never spans more than its own bucket + the neighbours it is smeared into.
    const faceArc = (this.R * Math.PI) / 2;
    this.G = Math.max(1, Math.floor(faceArc / (2 * COLLIDER_MAX_M)));
    this.cellWidthM = faceArc / this.G;
  }

  /** Feature count — telemetry / the idempotence oracle. */
  get featureCount(): number {
    return this.feats.length;
  }

  /**
   * Register one oriented box. The public ingestion seam: everything the field can
   * answer is derived from boxes added this way (or by a subclass calling
   * {@link pushBox} directly). Note `height` is the FULL height above `base`, not a
   * half-extent — the box spans [base, base + height] radially.
   */
  addBox(spec: BoxSpec): void {
    this.pushBox(
      spec.base[0], spec.base[1], spec.base[2],
      spec.up,
      spec.width[0], spec.width[1], spec.width[2],
      spec.halfWidth, spec.height, spec.halfDepth,
      !spec.standable,
      spec.planarSupport,
    );
  }

  /**
   * Register one oriented box. `bcx..` is the BASE-surface centre (a building's
   * grounded footprint centre / a wall segment's mid-base); the box centre is lifted
   * half its height along `up`, so it spans [base, base+2·hh] radially. Width axis
   * `wx..` is given; depth axis is up×width. Smeared into every bucket it reaches.
   */
  protected pushBox(
    bcx: number, bcy: number, bcz: number,
    up: number[],
    wxx: number, wxy: number, wxz: number,
    hw: number, height: number, hd: number,
    isWall: boolean,
    planarSupport = false,
  ): void {
    // depth = up × width (orthonormal frame; width already ⟂ up).
    let dxx = up[1] * wxz - up[2] * wxy;
    let dxy = up[2] * wxx - up[0] * wxz;
    let dxz = up[0] * wxy - up[1] * wxx;
    const dl = pmath.hypot(dxx, dxy, dxz) || 1;
    dxx /= dl; dxy /= dl; dxz /= dl;
    const hh = height * 0.5;
    const cx = bcx + up[0] * hh, cy = bcy + up[1] * hh, cz = bcz + up[2] * hh;
    const reach = pmath.hypot(hw, hh, hd) + QUERY_MARGIN_M;
    const fi = this.feats.length;
    this.feats.push({
      cx, cy, cz,
      ux: up[0], uy: up[1], uz: up[2],
      wx: wxx, wy: wxy, wz: wxz,
      dx: dxx, dy: dxy, dz: dxz,
      hw, hh, hd, reach, standable: !isWall, planarSupport,
      ex: Math.abs(wxx)*hw+Math.abs(up[0])*hh+Math.abs(dxx)*hd,
      ey: Math.abs(wxy)*hw+Math.abs(up[1])*hh+Math.abs(dxy)*hd,
      ez: Math.abs(wxz)*hw+Math.abs(up[2])*hh+Math.abs(dxz)*hd,
    });
    this.smear(fi);
  }

  // ── smear: insert a feature into every bucket its bound can reach ──────────────

  private stampDir(fi: number, dx: number, dy: number, dz: number, seen: Set<number>): void {
    const cell = cellOfG(dx, dy, dz, this.G);
    if (seen.has(cell)) return;
    seen.add(cell);
    let bucket = this.grid.get(cell);
    if (bucket === undefined) this.grid.set(cell, (bucket = []));
    bucket.push(fi);
  }

  private smear(fi: number): void {
    const f = this.feats[fi];
    // Bound reach + one bucket, so the disc covers every bucket a query within reach
    // of the centre can map to (mirrors carveindex's stampDisc; buckets ≫ reach).
    const rM = f.reach + this.cellWidthM;
    const fr = tangentFrame(f.cx, f.cy, f.cz);
    const seen = new Set<number>();
    this.stampDir(fi, f.cx, f.cy, f.cz, seen);
    for (let ring = 0; ring < 2; ring++) {
      const rr = rM * (ring === 0 ? 0.6 : 1.0);
      for (let s = 0; s < 8; s++) {
        const ang = (s / 8) * pmath.TWO_PI;
        const ou = pmath.cos(ang) * rr, ov = pmath.sin(ang) * rr;
        // Offset in the tangent plane, renormalized to a unit direction.
        const qx = f.cx + fr.t1[0] * ou + fr.t2[0] * ov;
        const qy = f.cy + fr.t1[1] * ou + fr.t2[1] * ov;
        const qz = f.cz + fr.t1[2] * ou + fr.t2[2] * ov;
        this.stampDir(fi, qx, qy, qz, seen);
      }
    }
  }

  // ── resolve (push-out) ─────────────────────────────────────────────────────────

  /**
   * Push a capsule of radius `radius` at planet-local (px,py,pz) OUT of any solid it
   * overlaps. Returns true and writes the CORRECTED position to `out` (mirroring the
   * push `resolveTownCollision` applies); false leaves `out` untouched. The single
   * bucket under the point supplies the candidate features.
   */
  resolve(px: number, py: number, pz: number, radius: number, out: { x: number; y: number; z: number }): boolean {
    const r = pmath.hypot(px, py, pz);
    if (r < 1e-6) return false;
    const bucket = this.grid.get(cellOfG(px / r, py / r, pz / r, this.G));
    if (bucket === undefined) return false;
    return this.resolveOver(bucket, px, py, pz, radius, out);
  }

  /** Reference/oracle push-out over ALL features (no bucket) — brute==hash proof. */
  resolveBrute(px: number, py: number, pz: number, radius: number, out: { x: number; y: number; z: number }): boolean {
    return this.resolveOver(this.allIndices(), px, py, pz, radius, out);
  }

  /** True when a capsule point overlaps a resident solid. */
  occludes(px: number, py: number, pz: number, radius = 0): boolean {
    const r = pmath.hypot(px, py, pz);
    if (r < 1e-6) return false;
    const bucket = this.grid.get(cellOfG(px / r, py / r, pz / r, this.G));
    return bucket !== undefined && this.insideAny(bucket, px, py, pz, radius);
  }

  /** Highest walkable box top under a planet-local point, as planet radius. */
  standRadius(px: number, py: number, pz: number): number | null {
    return this.standRadiusBelow(px, py, pz, Infinity);
  }

  /**
   * Highest standable top at the query's lateral spot whose radius is ≤
   * `maxR` — the height-aware stand query (interiors P3). The plain
   * standRadius (maxR = ∞) returns the GLOBAL highest top, which under an
   * overhang is the ROOF: the character's fall window then rejects it and
   * every standable surface BELOW it (an interior stair tread, a mezzanine)
   * became unreachable. Callers pass their catch ceiling (feet + fall window)
   * and get the surface actually underfoot.
   */
  standRadiusBelow(px: number, py: number, pz: number, maxR: number): number | null {
    const r = pmath.hypot(px, py, pz);
    if (r < 1e-6) return null;
    const bucket = this.grid.get(cellOfG(px / r, py / r, pz / r, this.G));
    if (bucket === undefined) return null;
    let best = -Infinity;
    for (const fi of bucket) {
      const f = this.feats[fi];
      if (!f.standable) continue;
      const tx = f.cx + f.ux * f.hh, ty = f.cy + f.uy * f.hh, tz = f.cz + f.uz * f.hh;
      const dot = px * f.ux + py * f.uy + pz * f.uz;
      if (f.planarSupport && dot <= 1e-6) continue;
      const scale = f.planarSupport ? (tx * f.ux + ty * f.uy + tz * f.uz) / dot : 1;
      const rx = px * scale - f.cx, ry = py * scale - f.cy, rz = pz * scale - f.cz;
      const qw = rx * f.wx + ry * f.wy + rz * f.wz;
      const qd = rx * f.dx + ry * f.dy + rz * f.dz;
      if (Math.abs(qw) > f.hw || Math.abs(qd) > f.hd) continue;
      const top = f.planarSupport ? r * scale : pmath.hypot(tx, ty, tz);
      if (top > best && top <= maxR) best = top;
    }
    return best > -Infinity ? best : null;
  }

  /** Terrain/roof clearance radius around a direction for ship transit planning. */
  clearanceRadius(dx: number, dy: number, dz: number, baseRadius: number, probeRadius = 0): number {
    const l = pmath.hypot(dx, dy, dz);
    if (l < 1e-6) return baseRadius;
    dx /= l; dy /= l; dz /= l;
    const bucket = this.grid.get(cellOfG(dx, dy, dz, this.G));
    if (bucket === undefined) return baseRadius;
    const px = dx * baseRadius, py = dy * baseRadius, pz = dz * baseRadius;
    let best = baseRadius;
    for (const fi of bucket) {
      const f = this.feats[fi];
      const rx = px - f.cx, ry = py - f.cy, rz = pz - f.cz;
      const qw = rx * f.wx + ry * f.wy + rz * f.wz;
      const qd = rx * f.dx + ry * f.dy + rz * f.dz;
      if (Math.abs(qw) > f.hw + probeRadius || Math.abs(qd) > f.hd + probeRadius) continue;
      const tx = f.cx + f.ux * f.hh, ty = f.cy + f.uy * f.hh, tz = f.cz + f.uz * f.hh;
      const top = pmath.hypot(tx, ty, tz);
      if (top > best) best = top;
    }
    return best;
  }

  private resolveOver(
    feats: number[],
    px: number, py: number, pz: number,
    radius: number,
    out: { x: number; y: number; z: number },
  ): boolean {
    // MULTI-PASS sequential push-out (2026-07-30, the corner-escape fix — the
    // homestead resolveObbs idiom): each pass finds the DEEPEST laterally
    // penetrated box and takes its CHEAPEST exit unconditionally; the next
    // pass resolves whatever that landed in. A convex corner (two
    // perpendicular walls) converges in two passes — both inward pushes.
    //
    // The old single-pass exit VETO ("skip an exit that lands inside another
    // box") was the escape hatch: pressed into a corner, the back-into-the-
    // room exit from wall A landed inside wall B and was vetoed, and with
    // P3's MERGED wall runs the along-wall exits are half a building long —
    // so the next-cheapest exit was OUT through the exterior face. Reported
    // from play as "players can escape out of the building in corners".
    //
    // Determinism: ascending-index iteration + strict `>` keeps the lowest-
    // index box on a tie per pass, so brute == hash still agree.
    let cx = px, cy = py, cz = pz;
    let moved = false;
    let ox = 0, oy = 0, oz = 0; // accumulated push (capped at the end)
    for (let pass = 0; pass < 3; pass++) {
      let bestFi = -1, bestPen = -1, bqw = 0, bqd = 0;
      for (const fi of feats) {
        const f = this.feats[fi];
        const rx = cx - f.cx, ry = cy - f.cy, rz = cz - f.cz;
        const qh = rx * f.ux + ry * f.uy + rz * f.uz;
        // Standable roofs exempt their walkable top band: a point on the roof
        // (qh near +hh) is STANDING, not embedded — the ground clamp owns it,
        // so resolve must not push it off. Walls gate their full height but
        // STOP at their top plane (2026-07-30 "barrier along the loft edge"):
        // ground-storey walls/partitions top out exactly at the upper floor's
        // walking plane, and the old `hh + radius` pad shoved bodies walking
        // the floor above along every buried wall line. Feet can never stand
        // ON a wall top (non-standable = the clamp won't ground there), so
        // nothing legitimate lived in that pad.
        const solidTop = f.standable ? f.hh - ROOF_STEP_M : f.hh - 0.05;
        if (qh > solidTop || qh < -f.hh - radius) continue;
        const qw = rx * f.wx + ry * f.wy + rz * f.wz;
        const qd = rx * f.dx + ry * f.dy + rz * f.dz;
        const ew = f.hw + radius, ed = f.hd + radius;
        if (qw <= -ew || qw >= ew || qd <= -ed || qd >= ed) continue;
        const pen = Math.min(ew - Math.abs(qw), ed - Math.abs(qd));
        if (pen > bestPen) { bestPen = pen; bestFi = fi; bqw = qw; bqd = qd; }
      }
      if (bestFi < 0) break;
      const f = this.feats[bestFi];
      const ew = f.hw + radius, ed = f.hd + radius;
      // Cheapest of the four lateral exits, a hair past the boundary (else
      // float rounding can leave the point epsilon-inside forever).
      const exits: [number, number, number, number][] = [
        [ew - bqw + 1e-3, f.wx, f.wy, f.wz],
        [ew + bqw + 1e-3, -f.wx, -f.wy, -f.wz],
        [ed - bqd + 1e-3, f.dx, f.dy, f.dz],
        [ed + bqd + 1e-3, -f.dx, -f.dy, -f.dz],
      ];
      exits.sort((a, b) => a[0] - b[0]);
      const [mag, nx, ny, nz] = exits[0];
      cx += nx * mag; cy += ny * mag; cz += nz * mag;
      ox += nx * mag; oy += ny * mag; oz += nz * mag;
      moved = true;
    }
    if (!moved) return false;
    // Cap the applied push at MAX_PUSH_M per call (the resolveTownCollision idiom):
    // deep penetration resolves in bounded steps, never a single multi-metre jump.
    const pl = pmath.hypot(ox, oy, oz);
    if (pl > MAX_PUSH_M) {
      const k = MAX_PUSH_M / pl;
      ox *= k; oy *= k; oz *= k;
    }
    out.x = px + ox; out.y = py + oy; out.z = pz + oz;
    return true;
  }

  /** True when (px,py,pz) sits inside ANY box in `feats` (capsule radius
   *  `radius`). FULL-height solid: occludes()/camera/bolt need the whole box.
   *  (resolveOver no longer consults this — its exit veto died with the
   *  corner-escape fix; pushes are multi-pass sequential now.) */
  private insideAny(feats: number[], px: number, py: number, pz: number, radius: number): boolean {
    const padding=radius*1.7320508075688772+1e-7;
    for (const fi of feats) {
      const f = this.feats[fi];
      const rx = px - f.cx, ry = py - f.cy, rz = pz - f.cz;
      if(Math.abs(rx)>f.ex+padding||Math.abs(ry)>f.ey+padding||Math.abs(rz)>f.ez+padding)continue;
      const qh = rx * f.ux + ry * f.uy + rz * f.uz;
      if (qh > f.hh + radius || qh < -f.hh - radius) continue;
      const qw = rx * f.wx + ry * f.wy + rz * f.wz;
      const qd = rx * f.dx + ry * f.dy + rz * f.dz;
      if (qw > -(f.hw + radius) && qw < f.hw + radius && qd > -(f.hd + radius) && qd < f.hd + radius) return true;
    }
    return false;
  }

  // ── segmentHit (sweep) ──────────────────────────────────────────────────────────

  /**
   * First fraction t∈[0,1] at which segment (ax,ay,az)→(bx,by,bz) enters a solid, or
   * -1 when clear. Candidate features are gathered from the buckets along the segment.
   */
  segmentHit(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius = 0): number {
    const feats = this.segCandidates(ax, ay, az, bx, by, bz);
    return this.segHitOver(feats, ax, ay, az, bx, by, bz, radius);
  }

  /** Reference/oracle sweep over ALL features (no buckets) — brute==hash proof. */
  segmentHitBrute(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius = 0): number {
    return this.segHitOver(this.allIndices(), ax, ay, az, bx, by, bz, radius);
  }

  /** Earliest inflated-box sweep plus outward contact normal. */
  segmentCast(
    ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius = 0,
  ): { t: number; n: [number, number, number] } | null {
    const feats = this.segCandidates(ax, ay, az, bx, by, bz);
    let bestT = Infinity, best: BoxFeat | null = null;
    for (const fi of feats) {
      const f = this.feats[fi];
      const t = this.segBox(f, ax, ay, az, bx, by, bz, radius);
      if (t >= 0 && t < bestT) { bestT = t; best = f; }
    }
    if (!best) return null;
    const hx = ax + (bx - ax) * bestT, hy = ay + (by - ay) * bestT, hz = az + (bz - az) * bestT;
    const rx = hx - best.cx, ry = hy - best.cy, rz = hz - best.cz;
    const q: [number, number, number] = [
      rx * best.wx + ry * best.wy + rz * best.wz,
      rx * best.ux + ry * best.uy + rz * best.uz,
      rx * best.dx + ry * best.dy + rz * best.dz,
    ];
    const ext: [number, number, number] = [best.hw + radius, best.hh + radius, best.hd + radius];
    let axis = 0, gap = Math.abs(Math.abs(q[0]) - ext[0]);
    for (let i = 1; i < 3; i++) {
      const candidate = Math.abs(Math.abs(q[i]) - ext[i]);
      if (candidate < gap) { gap = candidate; axis = i; }
    }
    const sign = q[axis] >= 0 ? 1 : -1;
    const basis = axis === 0
      ? [best.wx, best.wy, best.wz]
      : axis === 1 ? [best.ux, best.uy, best.uz] : [best.dx, best.dy, best.dz];
    return { t: bestT, n: [basis[0] * sign, basis[1] * sign, basis[2] * sign] };
  }

  /** Feature ids in every bucket the segment passes through (deduped). */
  private segCandidates(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number[] {
    // A cube-face cell is a convex cone. If both endpoints are in it, the
    // entire segment is too. Its stamped indices are already deduplicated and
    // in insertion order: corpse probes can use them without a Set/sort/copy.
    const ar=pmath.hypot(ax,ay,az),br=pmath.hypot(bx,by,bz);
    if(ar>=1e-6&&br>=1e-6){
      const cell=cellOfG(ax/ar,ay/ar,az/ar,this.G);
      if(cell===cellOfG(bx/br,by/br,bz/br,this.G))return this.grid.get(cell)??this.emptyBucket;
    }
    const out: number[] = [];
    const seen = new Set<number>();
    const push = (dx: number, dy: number, dz: number): void => {
      const r = pmath.hypot(dx, dy, dz);
      if (r < 1e-6) return;
      const bucket = this.grid.get(cellOfG(dx / r, dy / r, dz / r, this.G));
      if (bucket === undefined) return;
      for (const fi of bucket) if (!seen.has(fi)) { seen.add(fi); out.push(fi); }
    };
    const len = pmath.hypot(bx - ax, by - ay, bz - az);
    const steps = Math.max(1, Math.ceil(len / (this.cellWidthM * 0.5)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      push(ax + (bx - ax) * t, ay + (by - ay) * t, az + (bz - az) * t);
    }
    // Ascending order so the tie-break (lowest index) matches the brute scan.
    out.sort((p, q) => p - q);
    return out;
  }

  private segHitOver(feats: number[], ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius: number): number {
    let bestT = Infinity;
    for (const fi of feats) {
      const t = this.segBox(this.feats[fi], ax, ay, az, bx, by, bz, radius);
      if (t >= 0 && t < bestT) bestT = t; // ascending fi ⇒ lowest index wins ties
    }
    return bestT === Infinity ? -1 : bestT;
  }

  /** Slab (OBB) sweep: entry fraction of a→b into box `f`, or -1. */
  private segBox(f: BoxFeat, ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius: number): number {
    // Radius inflates all three local slabs. sqrt(3)*radius conservatively
    // bounds that inflation on every world axis, including tilted tube panels.
    const padding=radius*1.7320508075688772+1e-7;
    if(Math.min(ax,bx)>f.cx+f.ex+padding||Math.max(ax,bx)<f.cx-f.ex-padding
      ||Math.min(ay,by)>f.cy+f.ey+padding||Math.max(ay,by)<f.cy-f.ey-padding
      ||Math.min(az,bz)>f.cz+f.ez+padding||Math.max(az,bz)<f.cz-f.ez-padding)return -1;
    const dax = ax - f.cx, day = ay - f.cy, daz = az - f.cz;
    const dbx = bx - f.cx, dby = by - f.cy, dbz = bz - f.cz;
    const a0 = dax * f.wx + day * f.wy + daz * f.wz, b0 = dbx * f.wx + dby * f.wy + dbz * f.wz;
    const a1 = dax * f.ux + day * f.uy + daz * f.uz, b1 = dbx * f.ux + dby * f.uy + dbz * f.uz;
    const a2 = dax * f.dx + day * f.dy + daz * f.dz, b2 = dbx * f.dx + dby * f.dy + dbz * f.dz;
    let tMin = 0, tMax = 1;
    // width and depth slabs are symmetric [-h,h]; the height slab is [-hh,hh] too
    // (the box is centred at mid-height, so the base sits at -hh, roof at +hh).
    const s0 = clipSlab(a0, b0, -f.hw - radius, f.hw + radius, tMin, tMax); if (!s0) return -1; tMin = s0[0]; tMax = s0[1];
    const s1 = clipSlab(a1, b1, -f.hh - radius, f.hh + radius, tMin, tMax); if (!s1) return -1; tMin = s1[0]; tMax = s1[1];
    const s2 = clipSlab(a2, b2, -f.hd - radius, f.hd + radius, tMin, tMax); if (!s2) return -1; tMin = s2[0]; tMax = s2[1];
    if (tMin > tMax || tMin > 1 || tMax < 0) return -1;
    return Math.max(0, tMin);
  }

  private allIndices(): number[] {
    const a: number[] = new Array(this.feats.length);
    for (let i = 0; i < a.length; i++) a[i] = i;
    return a;
  }
}

/**
 * Clip parametric segment (s0→s1 along an axis) to the slab [lo,hi], intersected with
 * the running [tMin,tMax]. Returns the tightened [tMin,tMax] or null if it empties.
 */
function clipSlab(s0: number, s1: number, lo: number, hi: number, tMin: number, tMax: number): [number, number] | null {
  const d = s1 - s0;
  if (Math.abs(d) < 1e-9) return s0 >= lo && s0 <= hi ? [tMin, tMax] : null;
  let te = (lo - s0) / d;
  let tl = (hi - s0) / d;
  if (te > tl) { const tt = te; te = tl; tl = tt; }
  const nMin = te > tMin ? te : tMin;
  const nMax = tl < tMax ? tl : tMax;
  return nMin <= nMax ? [nMin, nMax] : null;
}
